import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IPricingAuditEntry,
  IPricingBulkProduct,
  IPricingBulkUpdateItem,
  IPricingCustomerLocationPricing,
  IPricingCustomerProductsWithPricingTier,
  IPricingGroup,
  IPricingGroupByLocationProducts,
  IPricingGroupCreate,
  IPricingGroupUpdate,
  IPricingLocationPricingTier,
  IProduct,
  IUpdateEntityProductCountryDetails,
} from '@boma/supplier-portal/shared/entities';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  addOrUpdateProductCountryDetails(
    entityId: string,
    entityCountryProductDetails: IUpdateEntityProductCountryDetails
  ) {
    return this.httpClient.post<IPaginatedResponse<IProduct>>(
      `${this.base}api/v${this.version}/${entityId}/Pricing`,
      {
        ...entityCountryProductDetails,
      }
    );
  }

  deletePricingTier(entityId, pricingTierId) {
    return this.httpClient.delete<string>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Tiers/${pricingTierId}`
    );
  }

  deletePricingTiers(entityId, pricingTierIds: string[]) {
    const data = {
      pricingTierIds: pricingTierIds,
    };
    return this.httpClient.delete<string>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Tiers/Batch`,
      {
        body: data,
      }
    );
  }

  deletePricingGroup(entityId, id) {
    return this.httpClient.delete<string>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/${id}`
    );
  }

  listPricingAudit(
    entityId,
    params: {
      customerIds?: string[];
      locationIds?: string[];
      productIds?: string[];
      userIds?: string[];
      startDate?: string;
      endDate?: string;
    },
    page = 1,
    perPage = 25
  ) {
    const queryParams = [`page=${page}`, `pageSize=${perPage}`];
    const urlQuery = `?${queryParams.join('&')}`;

    return this.httpClient.post<IPaginatedResponse<IPricingAuditEntry>>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Audit/List${urlQuery}`,
      params
    );
  }

  loadPricingGroups(
    entityId: string,
    params: {
      customerIds: string[];
      locationIds: string[];
      productIds: string[];
      groupBy?: string;
      deletedEntries?: boolean;
      promotions?: boolean;
    }
  ) {
    if (!params['groupBy']) params['groupBy'] = 'Location';
    return this.httpClient.post<IPricingGroup[]>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Group/List`,
      params
    );
  }

  createPricingGroup(params: IPricingGroupCreate) {
    return this.httpClient.post<IPricingGroupByLocationProducts>(
      `${this.base}api/v${this.version}/${params.entityId}/Pricing/Group`,
      params
    );
  }

  updatePricingGroup(id, params: IPricingGroupUpdate) {
    return this.httpClient.put<IPricingGroupUpdate>(
      `${this.base}api/v${this.version}/${params.entityId}/Pricing/Group/${id}`,
      params
    );
  }

  getProductsForBulkUpdate(
    entityId: string, //required
    locationIds: string[] = [],
    definitionsFilters: { targets: number; definitionIds: string[] },
    productIds: string[] = [],
    categoryIds: string[] = [],
    brandIds: string[] = [],
    departmentIds: string[] = []
  ) {
    const params = {
      definitionsFilters,
      productFilters: {
        brandIds: brandIds,
        categoryIds: categoryIds,
        departmentIds: departmentIds,
      },
      productIds: productIds,
      locationIds: locationIds,
    };
    return this.httpClient.post<IPricingBulkProduct[]>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/List`,
      params
    );
  }

  //Location Product Pricing Tiers

  listLocationProductPricingTiers(
    entityId: string,
    params: {
      locationIds: string[];
      productIds: string[];
    }
  ) {
    // TODO THIS DATA TYPE RETURNED HERE SHOULD BE CHANGED TO A TIER ONE
    return this.httpClient.post<IPaginatedResponse<IPricingBulkProduct>>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/List/LocationProductPricingTiers`,
      params
    );
  }

  saveLocationProductPricingTiers(
    entityId: string,
    params: {
      locationPricingTiers: IPricingLocationPricingTier[];
    }
  ) {
    return this.httpClient.post<{ items }>(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Save/LocationProductPricingTiers`,
      params
    );
  }

  updateBulkProducts(entityId: string, params: IPricingBulkUpdateItem[]) {
    return this.httpClient.put<any[]>(
      `${this.base}api/v${this.version}/${entityId}/Pricing`,
      params
    );
  }

  getPricingTiers(
    entityId: string, //required
    params: {
      customerIds: string[];
      locationIds: string[];
      productIds: string[];
      groupBy?: string;
      deletedEntries?: boolean;
      promotions?: boolean;
      definitionsFilters?: { targets: number; definitionIds: string[] };
      productFilters: {
        brandIds: string[];
        categoryIds: string[];
        departmentIds: string[];
      };
    },
    page = 1,
    perPage = 1000
  ) {
    const queryParams = [];
    let urlQuery = '';

    if (page) queryParams.push(`page=${page}`);

    if (perPage) queryParams.push(`pageSize=${perPage}`); // TODO api should always use perPage

    if (queryParams.length > 0) urlQuery = `?${queryParams.join('&')}`;

    //TODO CREATE AN INTERFACE FOR THIS RESPONSE
    return this.httpClient.post<
      IPaginatedResponse<IPricingCustomerProductsWithPricingTier>
    >(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Tiers/List${urlQuery}`,
      params
    );
  }

  //This is a test result file for getPricingTiers so we dont have to whack the API all the time
  getPricingTiersTest(): Observable<any> {
    return this.httpClient.get('./assets/data/pricing-tiers.json');
  }

  getCustomerLocationPricing(
    entityId: string,
    customerId: string,
    customerLocationId: string,
    params: {
      productIds: string[];
    } = {
      productIds: [],
    },
    page: string = '1',
    perPage: string = '250' // Todo make 250 later
  ) {
    const queryParams = [];
    let urlQuery = '';
    console.log(customerLocationId);
    if (customerLocationId === 'all') customerLocationId = null;
    if (page) {
      queryParams.push(`page=${page}`);
    }

    if (perPage) {
      queryParams.push(`pageSize=${perPage}`);
    }

    if (queryParams.length > 0) {
      urlQuery = `?${queryParams.join('&')}`;
    }

    return this.httpClient.post<
      IPaginatedResponse<IPricingCustomerLocationPricing>
    >(
      `${this.base}api/v${this.version}/${entityId}/Pricing/Customer/${customerId}/${customerLocationId}${urlQuery}`,
      params
    );
  }
}
